import React, { useState, useEffect } from 'react';
import { X, Bot, AlertTriangle, CheckCircle, RefreshCw, Save, Key, Lightbulb, Users, Sparkles, Brain, Cake, CalendarDays, Clock, Star, TrendingUp, Heart, MapPin, Globe } from 'lucide-react';

const API = 'https://qbdp-backend-production.up.railway.app';

const PRIORITY_MAP = {
  haute:       { label: 'URGENT',      bg: '#fef2f2', border: '#fecaca', color: '#dc2626', dot: '#dc2626' },
  moyenne:     { label: 'À FAIRE',     bg: '#fffbeb', border: '#fde68a', color: '#d97706', dot: '#f59e0b' },
  opportunite: { label: 'OPPORTUNITÉ', bg: '#eff6ff', border: '#bfdbfe', color: '#1a56db', dot: '#3b82f6' },
};

const TYPE_SECTION = {
  dormants:    'Intelligence prédictive',
  vip:         'Intelligence prédictive',
  anticipation:'Intelligence prédictive',
  birthday:    'Anniversaires',
  anniv_sejour:'Anniversaires',
  event:       'Événements régionaux',
};

// ── Modal génération + création campagne ──────────────────────────────────────
function ModalGenerer({ opp, onClose, onCreated, showToast }) {
  const [step, setStep]       = useState('loading'); // loading | preview | saving
  const [generated, setGen]   = useState(null);
  const [error, setError]     = useState(null);
  const token = sessionStorage.getItem('qbdp_token');

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/ai/generate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ context: opp.context }),
        });
        const d = await r.json();
        if (!r.ok) { setError(d.error || 'Erreur'); setStep('error'); return; }
        setGen(d);
        setStep('preview');
      } catch { setError('Erreur réseau'); setStep('error'); }
    })();
  }, []);

  const handleCreate = async () => {
    setStep('saving');
    try {
      const body = {
        name:     generated.nom     || opp.titre,
        subject:  generated.subject || '',
        content:  generated.content || '',
        template: generated.template || 'default',
        type:     'email',
        cible:    'clients',
        status:   'draft',
        recipient_ids: opp.destinataires,
      };
      const r = await fetch(`${API}/api/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) { showToast(d.error || 'Erreur création', false); setStep('preview'); return; }
      showToast('✅ Campagne créée ! Retrouvez-la dans Communications.');
      onCreated();
      onClose();
    } catch { showToast('Erreur réseau', false); setStep('preview'); }
  };

  const ovl = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };
  const box = {
    background: '#fff', borderRadius: 20, width: 680, maxHeight: '90vh',
    overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', padding: 32,
  };

  return (
    <div style={ovl} onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={box}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div style={{ fontWeight: 700, fontSize: 17, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 8 }}><Sparkles size={17} color="#1a56db" /> Génération par IA</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}><X size={20} /></button>
        </div>

        {step === 'loading' && (
          <div style={{ textAlign: 'center', padding: '60px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}><Bot size={48} color="#1a56db" /></div>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#1a1a2e', marginBottom: 8 }}>L'IA rédige votre campagne...</div>
            <div style={{ fontSize: 13, color: '#9ca3af' }}>Analyse du contexte et rédaction en cours</div>
            <div style={{ marginTop: 24, display: 'flex', justifyContent: 'center', gap: 6 }}>
              {[0,1,2].map(i => (
                <div key={i} style={{
                  width: 8, height: 8, borderRadius: '50%', background: '#1a56db',
                  animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
          </div>
        )}

        {step === 'error' && (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12 }}><AlertTriangle size={40} color="#dc2626" /></div>
            <div style={{ color: '#dc2626', fontWeight: 600, marginBottom: 8 }}>Erreur de génération</div>
            <div style={{ color: '#6b7280', fontSize: 13, marginBottom: 20 }}>{error}</div>
            {error?.includes('GROQ') && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#92400e', textAlign: 'left' }}>
                Ajoutez <strong>GROQ_API_KEY</strong> dans Railway (gratuit sur <a href="https://console.groq.com" target="_blank" rel="noreferrer">console.groq.com</a>).
              </div>
            )}
            <button onClick={onClose} style={{ marginTop: 20, padding: '10px 20px', borderRadius: 10, border: 'none', background: '#f3f4f6', color: '#374151', cursor: 'pointer', fontWeight: 600 }}>Fermer</button>
          </div>
        )}

        {(step === 'preview' || step === 'saving') && generated && (
          <div>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#15803d', display: 'flex', alignItems: 'center', gap: 8 }}>
              <CheckCircle size={14} /> Contenu généré — vous pouvez le modifier avant de créer le brouillon
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Nom de la campagne</label>
              <input
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box' }}
                value={generated.nom || ''}
                onChange={e => setGen(g => ({ ...g, nom: e.target.value }))}
              />
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Objet de l'email</label>
              <input
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box' }}
                value={generated.subject || ''}
                onChange={e => setGen(g => ({ ...g, subject: e.target.value }))}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Message</label>
              <textarea
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #e5e7eb', fontSize: 14, minHeight: 160, resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.6 }}
                value={generated.content || ''}
                onChange={e => setGen(g => ({ ...g, content: e.target.value }))}
              />
            </div>

            {opp.destinataires?.length > 0 && (
              <div style={{ background: '#eff6ff', borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 13, color: '#1a56db', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Users size={14} /> {opp.destinataires.length} destinataire{opp.destinataires.length > 1 ? 's' : ''} pré-sélectionné{opp.destinataires.length > 1 ? 's' : ''} — modifiables dans Communications
              </div>
            )}
            {opp.type === 'event' && (
              <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 16px', marginBottom: 20, fontSize: 12, color: '#92400e', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Lightbulb size={13} /> Événement : sélectionnez les destinataires dans Communications après la création.
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
              <button onClick={onClose} style={{ padding: '11px 20px', borderRadius: 10, border: 'none', background: '#f3f4f6', color: '#374151', cursor: 'pointer', fontWeight: 600 }}>Annuler</button>
              <button onClick={handleCreate} disabled={step === 'saving'}
                style={{ padding: '11px 24px', borderRadius: 10, border: 'none', background: '#1e3a8a', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
                {step === 'saving' ? 'Création...' : <><Save size={14} /> Créer le brouillon</>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const OPP_ICON = {
  dormants:    Clock,
  vip:         Star,
  anticipation: TrendingUp,
  birthday:    Cake,
  anniv_sejour: Heart,
  event:       CalendarDays,
};

// ── Carte opportunité ─────────────────────────────────────────────────────────
function CarteOpp({ opp, onGenerer }) {
  const p = PRIORITY_MAP[opp.priority] || PRIORITY_MAP.opportunite;
  const OppIcon = OPP_ICON[opp.type] || Lightbulb;
  return (
    <div style={{
      background: '#fff', borderRadius: 14, border: `1px solid ${p.border}`,
      padding: '18px 20px', display: 'flex', alignItems: 'flex-start', gap: 14,
      boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
    }}>
      <div style={{ flexShrink: 0, marginTop: 2, width: 32, height: 32, borderRadius: 8, background: p.bg, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <OppIcon size={18} color={p.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#1a1a2e' }}>{opp.titre}</span>
          <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: p.bg, color: p.color, border: `1px solid ${p.border}` }}>
            {p.label}
          </span>
          {opp.count > 0 && (
            <span style={{ fontSize: 11, color: '#6b7280', background: '#f3f4f6', padding: '2px 8px', borderRadius: 20, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <Users size={11} /> {opp.count} client{opp.count > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.5, marginBottom: opp.type === 'event' ? 10 : 14 }}>{opp.description}</div>
        {opp.type === 'event' && (opp.date || opp.lieu || opp.url) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 }}>
            {opp.date && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#1a56db', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 20, padding: '3px 10px' }}>
                <CalendarDays size={11} /> {opp.date}
              </span>
            )}
            {opp.lieu && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#374151', background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 20, padding: '3px 10px' }}>
                <MapPin size={11} /> {opp.lieu}
              </span>
            )}
            {opp.url && (
              <a href={opp.url} target="_blank" rel="noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#16a34a', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 20, padding: '3px 10px', textDecoration: 'none' }}>
                <Globe size={11} /> Site officiel
              </a>
            )}
          </div>
        )}
        <button onClick={() => onGenerer(opp)}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', borderRadius: 10, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg, #1e3a8a, #1a56db)', color: '#fff', fontWeight: 700, fontSize: 13 }}>
          <Sparkles size={14} /> Générer la campagne avec l'IA
        </button>
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────────────────
export default function CopiloteIA({ onNavigate }) {
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [genOpp, setGenOpp]     = useState(null);
  const [toast, setToast]       = useState(null);
  const [analyzedAt, setAt]     = useState(null);
  const token = sessionStorage.getItem('qbdp_token');

  const showToast = (msg, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4000);
  };

  const analyser = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/ai/opportunities`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const d = await r.json();
      if (!r.ok) { showToast(d.error || 'Erreur', false); } else {
        setData(d);
        setAt(new Date());
      }
    } catch { showToast('Erreur réseau', false); }
    setLoading(false);
  };

  useEffect(() => { analyser(); }, []);

  // Regrouper par section
  const sections = data ? [
    {
      id: 'intel',
      Icon: Brain,
      label: 'Intelligence prédictive',
      desc: 'Analyse comportementale de vos clients',
      items: data.opportunities.filter(o => TYPE_SECTION[o.type] === 'Intelligence prédictive'),
    },
    {
      id: 'anniv',
      Icon: Cake,
      label: 'Anniversaires',
      desc: 'Naissance et anniversaire de premier séjour',
      items: data.opportunities.filter(o => TYPE_SECTION[o.type] === 'Anniversaires'),
    },
    {
      id: 'events',
      Icon: CalendarDays,
      label: 'Événements régionaux',
      desc: data?.ville ? `Veille automatique — ${data.ville}` : 'Veille automatique de votre région',
      items: data.opportunities.filter(o => TYPE_SECTION[o.type] === 'Événements régionaux'),
      noKey: !data?.hasTavily,
    },
  ] : [];

  const total = data?.opportunities?.length || 0;
  const urgents = data?.opportunities?.filter(o => o.priority === 'haute').length || 0;

  return (
    <div style={{ padding: 32, maxWidth: 1000, margin: '0 auto', paddingBottom: 100 }}>
      <style>{`@keyframes pulse { 0%,100%{opacity:.3;transform:scale(.8)} 50%{opacity:1;transform:scale(1)} }`}</style>

      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 3000, padding: '12px 20px', borderRadius: 12,
          background: toast.ok ? '#f0fdf4' : '#fef2f2', color: toast.ok ? '#16a34a' : '#dc2626',
          border: `1px solid ${toast.ok ? '#bbf7d0' : '#fecaca'}`, fontWeight: 600, fontSize: 13,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
          {toast.msg}
        </div>
      )}

      {genOpp && (
        <ModalGenerer
          opp={genOpp}
          onClose={() => setGenOpp(null)}
          onCreated={() => {}}
          showToast={showToast}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
        <div>
          <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1a1a2e', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Bot size={24} color="#1a56db" /> Copilote IA
          </h1>
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 4 }}>
            Opportunités détectées automatiquement à partir de vos données clients
            {data?.secteurLabel && <span style={{ marginLeft: 8, fontSize: 11, background: '#eff6ff', color: '#1a56db', padding: '2px 10px', borderRadius: 20, fontWeight: 600 }}>{data.secteurLabel}</span>}
          </p>
          {analyzedAt && (
            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
              Dernière analyse : {analyzedAt.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </p>
          )}
        </div>
        <button onClick={analyser} disabled={loading}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 20px', borderRadius: 12, border: 'none', cursor: loading ? 'not-allowed' : 'pointer', background: '#1e3a8a', color: '#fff', fontWeight: 700, fontSize: 13, opacity: loading ? 0.7 : 1 }}>
          {loading ? 'Analyse en cours...' : <><RefreshCw size={14} /> Analyser maintenant</>}
        </button>
      </div>

      {/* Compteurs */}
      {data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12, marginBottom: 28 }}>
          {[
            { label: 'Opportunités détectées', val: total,   Icon: Lightbulb,   color: '#1a56db', bg: '#eff6ff' },
            { label: 'Actions urgentes',        val: urgents, Icon: AlertTriangle, color: '#dc2626', bg: '#fef2f2' },
            { label: 'Événements régionaux',    val: data.opportunities.filter(o => o.type === 'event').length, Icon: CalendarDays, color: '#d97706', bg: '#fffbeb' },
          ].map(s => (
            <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '16px 20px' }}>
              <s.Icon size={20} color={s.color} />
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.val}</div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>{s.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Loading */}
      {loading && !data && (
        <div style={{ textAlign: 'center', padding: '80px 0' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}><Bot size={48} color="#1a56db" /></div>
          <div style={{ fontWeight: 600, color: '#1a1a2e', fontSize: 16, marginBottom: 8 }}>Analyse de vos données en cours...</div>
          <div style={{ fontSize: 13, color: '#9ca3af' }}>RFM clients · Anniversaires · Événements régionaux</div>
        </div>
      )}

      {/* Sections */}
      {data && sections.map(section => (
        <div key={section.id} style={{ marginBottom: 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <section.Icon size={18} color="#1a56db" />
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a2e', margin: 0 }}>{section.label}</h2>
            {section.items.length > 0 && (
              <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: '#f3f4f6', color: '#6b7280' }}>
                {section.items.length}
              </span>
            )}
          </div>
          <p style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14, marginTop: 0 }}>{section.desc}</p>

          {section.noKey ? (
            <div style={{ background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 12, padding: '24px', textAlign: 'center' }}>
              <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><Key size={32} color="#9ca3af" /></div>
              <div style={{ fontWeight: 600, color: '#374151', marginBottom: 4 }}>Clé Tavily non configurée</div>
              <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 12 }}>
                Ajoutez <code style={{ background: '#e5e7eb', padding: '2px 6px', borderRadius: 4 }}>TAVILY_API_KEY</code> dans Railway pour activer la veille événementielle.
              </div>
              <a href="https://tavily.com" target="_blank" rel="noreferrer"
                style={{ fontSize: 12, color: '#1a56db', textDecoration: 'none', fontWeight: 600 }}>
                Obtenir une clé gratuite Tavily (1 000 recherches/mois) →
              </a>
            </div>
          ) : section.items.length === 0 ? (
            <div style={{ background: '#f9fafb', border: '1px dashed #e5e7eb', borderRadius: 12, padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
              {section.id === 'events' ? 'Aucun événement trouvé pour votre région ce mois-ci' : 'Aucune action requise pour le moment'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {section.items.map(opp => (
                <CarteOpp key={opp.id} opp={opp} onGenerer={setGenOpp} />
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Infos API */}
      {data && !data.hasGroq && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, padding: '16px 20px', marginTop: 8 }}>
          <div style={{ fontWeight: 600, color: '#dc2626', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 }}><AlertTriangle size={15} /> Génération IA non activée</div>
          <div style={{ fontSize: 13, color: '#6b7280' }}>
            Ajoutez <code style={{ background: '#fee2e2', padding: '2px 6px', borderRadius: 4 }}>GROQ_API_KEY</code> dans Railway pour activer la rédaction automatique (gratuit — <a href="https://console.groq.com" target="_blank" rel="noreferrer" style={{ color: '#1a56db' }}>console.groq.com</a>).
          </div>
        </div>
      )}

      {onNavigate && (
        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <button onClick={() => onNavigate('/communications')}
            style={{ padding: '10px 20px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#fff', color: '#374151', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            → Voir toutes mes campagnes
          </button>
        </div>
      )}
    </div>
  );
}
